<script>
  import { createEventDispatcher } from 'svelte';
  import { Upload, X, FileCheck, CheckCircle2, XCircle, AlertTriangle } from 'lucide-svelte';
  import { _ } from '../lib/i18n.js';
  import { proposalsApi } from '../stores/api.js';

  export let proposalId = null;
  export let proposalNumber = '';

  const dispatch = createEventDispatcher();

  let file = null;
  let notes = '';
  let loading = false;
  let error = null;
  let uploadResult = null;
  let dragOver = false;

  function handleFileSelect(event) {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
    }
  }

  function handleDrop(event) {
    event.preventDefault();
    dragOver = false;
    const droppedFile = event.dataTransfer.files?.[0];
    if (droppedFile) {
      setFile(droppedFile);
    }
  }

  function handleDragOver(event) {
    event.preventDefault();
    dragOver = true;
  }

  function handleDragLeave() {
    dragOver = false;
  }

  function setFile(selectedFile) {
    const ext = selectedFile.name.toLowerCase().slice(selectedFile.name.lastIndexOf('.'));
    if (ext !== '.pdf') {
      error = $_('manualConfirm.pdfOnly');
      file = null;
      return;
    }
    file = selectedFile;
    error = null;
    uploadResult = null;
  }

  function clearFile() {
    file = null;
    error = null;
    uploadResult = null;
  }

  async function handleConfirm() {
    if (!file) return;

    loading = true;
    error = null;
    uploadResult = null;

    try {
      console.log(`[ManualConfirmModal] Manually confirming proposal ${proposalId}`);

      const formData = new FormData();
      formData.append('pdf', file);
      if (notes.trim()) {
        formData.append('notes', notes.trim());
      }

      const response = await proposalsApi.manualConfirm(proposalId, formData);

      console.log(`[ManualConfirmModal] Manual confirmation successful:`, response);
      uploadResult = {
        success: true,
        message: response.message
      };

      // Notify parent of successful confirmation
      dispatch('confirmed', { proposalId });
    } catch (err) {
      console.error('[ManualConfirmModal] Error:', err);
      error = err.message || $_('manualConfirm.error');
    } finally {
      loading = false;
    }
  }

  function handleClose() {
    dispatch('close');
  }

  function handleKeydown(event) {
    if (event.key === 'Escape' && !loading) {
      handleClose();
    }
  }

  function formatFileSize(bytes) {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }
</script>

<svelte:window on:keydown={handleKeydown} />

<!-- Modal backdrop -->
<div class="fixed inset-0 z-50 flex items-center justify-center">
  <div class="fixed inset-0 bg-black/40 backdrop-blur-sm" on:click={handleClose}></div>

  <!-- Modal -->
  <div class="relative bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-xl w-full mx-4 scale-in">
    <!-- Header -->
    <div class="px-6 py-5 border-b border-gray-200/60 dark:border-gray-700/60 flex items-center justify-between bg-linear-to-r from-amber-50/50 dark:from-amber-900/30 to-transparent rounded-t-2xl">
      <div class="flex items-center space-x-3">
        <div class="w-10 h-10 bg-linear-to-br from-amber-500 to-amber-600 rounded-xl flex items-center justify-center">
          <FileCheck class="h-5 w-5 text-white" />
        </div>
        <div>
          <h2 class="text-lg font-bold text-gray-900 dark:text-white">{$_('manualConfirm.title')}</h2>
          <p class="text-sm text-gray-500 dark:text-gray-400">{$_('manualConfirm.forProposal')} {proposalNumber || proposalId}</p>
        </div>
      </div>
      {#if !loading}
        <button
          on:click={handleClose}
          class="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-all duration-200"
        >
          <X size={20} />
        </button>
      {/if}
    </div>

    <!-- Content -->
    <div class="px-6 py-4">
      <!-- Warning box -->
      <div class="bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-lg p-4 mb-4">
        <div class="flex items-start">
          <AlertTriangle class="h-5 w-5 text-amber-500 dark:text-amber-400 mt-0.5 mr-3 flex-shrink-0" />
          <div class="text-sm text-amber-700 dark:text-amber-300">
            <p class="font-medium">{$_('manualConfirm.warning')}</p>
            <p class="mt-1">{$_('manualConfirm.warningDetail')}</p>
            <!-- Behaviour is unchanged; only the labelling is. The document is
                 recorded as a manual confirmation in your name and is never
                 presented anywhere as a digital signature. -->
            <p class="mt-1">{$_('manualConfirm.labelNotice')}</p>
          </div>
        </div>
      </div>

      <!-- Drop zone -->
      {#if !file && !uploadResult?.success}
        <div
          class="border-2 border-dashed rounded-xl p-8 text-center transition-colors duration-200 {dragOver ? 'border-amber-400 bg-amber-50 dark:bg-amber-900/20' : 'border-gray-300 dark:border-gray-600'}"
          on:drop={handleDrop}
          on:dragover={handleDragOver}
          on:dragleave={handleDragLeave}
          role="region"
        >
          <Upload class="h-12 w-12 mx-auto text-gray-400 dark:text-gray-500 mb-4" />
          <p class="text-gray-600 dark:text-gray-300 mb-2">{$_('manualConfirm.dragDrop')}</p>
          <p class="text-gray-500 dark:text-gray-400 text-sm mb-4">{$_('manualConfirm.or')}</p>
          <label class="btn btn-primary cursor-pointer">
            {$_('manualConfirm.browse')}
            <input
              type="file"
              accept=".pdf,application/pdf"
              class="hidden"
              on:change={handleFileSelect}
            />
          </label>
        </div>
      {/if}

      <!-- Selected file -->
      {#if file && !uploadResult?.success}
        <div class="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 flex items-center justify-between mb-4">
          <div class="flex items-center">
            <FileCheck class="h-8 w-8 text-amber-500 mr-3" />
            <div>
              <p class="font-medium text-gray-900 dark:text-white">{file.name}</p>
              <p class="text-sm text-gray-500 dark:text-gray-400">{formatFileSize(file.size)}</p>
            </div>
          </div>
          {#if !loading}
            <button
              on:click={clearFile}
              class="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
            >
              <X size={20} />
            </button>
          {/if}
        </div>

        <!-- Notes field -->
        <div class="mb-4">
          <label class="label">{$_('manualConfirm.notesLabel')}</label>
          <textarea
            class="input w-full h-20"
            bind:value={notes}
            placeholder={$_('manualConfirm.notesPlaceholder')}
            disabled={loading}
          ></textarea>
        </div>
      {/if}

      <!-- Error message -->
      {#if error}
        <div class="mt-4 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg">
          <div class="flex items-start">
            <XCircle class="h-5 w-5 text-red-500 dark:text-red-400 mt-0.5 mr-2 flex-shrink-0" />
            <p class="text-sm text-red-700 dark:text-red-300">{error}</p>
          </div>
        </div>
      {/if}

      <!-- Success result -->
      {#if uploadResult?.success}
        <div class="mt-4 p-4 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg">
          <div class="flex items-start">
            <CheckCircle2 class="h-5 w-5 text-green-500 dark:text-green-400 mt-0.5 mr-2 flex-shrink-0" />
            <div>
              <p class="text-sm font-medium text-green-700 dark:text-green-300">{$_('manualConfirm.success')}</p>
              <p class="text-sm text-green-600 dark:text-green-400 mt-1">{$_('manualConfirm.successDetail')}</p>
              <p class="text-sm text-green-600 dark:text-green-400 mt-1">{$_('manualConfirm.successLabelling')}</p>
            </div>
          </div>
        </div>
      {/if}
    </div>

    <!-- Footer -->
    <div class="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3 rounded-b-2xl">
      <button
        type="button"
        on:click={handleClose}
        class="btn btn-secondary"
        disabled={loading}
      >
        {uploadResult?.success ? $_('common.close') : $_('common.cancel')}
      </button>
      {#if file && !uploadResult?.success}
        <button
          type="button"
          on:click={handleConfirm}
          class="btn bg-amber-500 hover:bg-amber-600 text-white"
          disabled={loading}
        >
          {#if loading}
            <svg class="animate-spin -ml-1 mr-3 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            {$_('manualConfirm.confirming')}
          {:else}
            {$_('manualConfirm.confirmButton')}
          {/if}
        </button>
      {/if}
    </div>
  </div>
</div>
